import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppShell from "../components/app/AppShell";
import ReadinessGauge from "../components/app/ReadinessGauge";
import { api } from "../lib/api";
import { useT } from "../contexts/LanguageContext";
import { toast } from "sonner";
import { CheckCircle, XCircle, Sparkle } from "@phosphor-icons/react";

const LETTERS = ["A", "B", "C", "D"];

export default function Results() {
  const { t, tDomain, lang } = useT();
  const { attemptId } = useParams();
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/attempts/${attemptId}`, { params: { lang } });
        setAttempt(data);
      } catch (e) {
        toast.error(t("results.could_not_load"));
      } finally {
        setLoading(false);
      }
    })();
  }, [attemptId, lang]); // eslint-disable-line

  if (loading) {
    return (
      <AppShell>
        <div className="h-64 grid place-items-center" data-testid="results-loading">
          <div className="h-10 w-10 rounded-full border-2 border-slate-200 border-t-[#002FA7] animate-spin" />
        </div>
      </AppShell>
    );
  }
  if (!attempt) return null;

  const ai = attempt.ai_analysis || {};
  const answersByQ = Object.fromEntries(attempt.answers.map((a) => [a.question_id, a]));

  return (
    <AppShell>
      <div className="overline text-[#002FA7]">{t("results.overline_results")} · {attempt.mode.toUpperCase()}</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="results-title">{t("results.title")}</h1>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="card lg:col-span-4 grid place-items-center text-center">
          <div className="overline">{t("dash.readiness")}</div>
          <div className="mt-2"><ReadinessGauge value={attempt.readiness_score} label={ai.readiness_level || ""} /></div>
          <div className="mt-2 grid grid-cols-2 gap-6">
            <div>
              <div className="overline">{t("results.score")}</div>
              <div className="font-display text-2xl font-bold" data-testid="results-score">{attempt.score_pct}%</div>
            </div>
            <div>
              <div className="overline">{t("common.questions")}</div>
              <div className="font-display text-2xl font-bold">{attempt.questions.length}</div>
            </div>
          </div>
        </div>

        <div className="card lg:col-span-4">
          <div className="overline">{t("results.domain_breakdown")}</div>
          <h3 className="font-display text-xl font-semibold mt-1">PMBOK 8</h3>
          <div className="mt-4 space-y-3">
            {Object.entries(attempt.domain_scores || {}).map(([d, v]) => (
              <div key={d}>
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{tDomain(d)}</span>
                  <span className="font-mono">{v}%</span>
                </div>
                <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${v < 60 ? "bg-[#E11D48]" : v < 75 ? "bg-[#D97706]" : "bg-[#002FA7]"}`} style={{ width: `${v}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card lg:col-span-4">
          <div className="overline flex items-center gap-1 text-[#002FA7]"><Sparkle size={12} weight="fill" /> {t("results.ai_overline")}</div>
          <h3 className="font-display text-xl font-semibold mt-1">{t("results.diagnosis")}</h3>
          <p className="text-sm text-slate-700 mt-3 leading-relaxed" data-testid="ai-summary">{ai.summary}</p>
          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="overline">{t("results.weak")}</div>
              <div className="text-[#E11D48] font-semibold">{(ai.weak_domains || []).map(tDomain).join(", ")}</div>
            </div>
            <div>
              <div className="overline">{t("results.strong")}</div>
              <div className="text-[#059669] font-semibold">{(ai.strength_areas || []).map(tDomain).join(", ")}</div>
            </div>
          </div>
        </div>

        <div className="card lg:col-span-12">
          <div className="overline">{t("results.improvement")}</div>
          <h3 className="font-display text-xl font-semibold mt-1">{t("results.next_steps")}</h3>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            {(ai.improvement_suggestions || []).map((s, i) => (
              <li key={i} className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-[#002FA7] mt-0.5" /> {s}</li>
            ))}
          </ul>
          <div className="mt-5 flex gap-3 flex-wrap">
            <Link to="/study-plan" className="btn-primary" data-testid="results-view-plan">{t("results.update_plan")}</Link>
            <Link to="/practice" className="btn-secondary" data-testid="results-practice-again">{t("results.practice_weak")}</Link>
          </div>
        </div>

        <div className="card lg:col-span-12">
          <div className="overline">{t("results.review_count", { n: attempt.questions.length })}</div>
          <div className="mt-4 space-y-4">
            {(attempt.question_details || []).map((q, qi) => {
              if (!q) return null;
              const a = answersByQ[q.question_id];
              const sel = a?.selected_index;
              return (
                <div key={q.question_id} className="border-b border-slate-100 pb-4 last:border-0" data-testid={`review-q-${qi}`}>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="overline">Q{qi + 1}</span>
                    <span className="overline text-slate-400">·</span>
                    <span className="overline">{tDomain(q.domain)}</span>
                    {a?.is_correct ? <CheckCircle size={14} weight="fill" className="text-[#059669]" /> : <XCircle size={14} weight="fill" className="text-[#E11D48]" />}
                  </div>
                  <div className="mt-1 font-medium">{q.text}</div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {q.options.map((opt, i) => {
                      let cls = "option-tile !py-2 !text-sm";
                      if (i === q.correct_index) cls += " correct";
                      else if (sel === i) cls += " incorrect";
                      return (
                        <div key={i} className={cls}>
                          <span className="option-letter !w-6 !h-6 !text-xs">{LETTERS[i]}</span>
                          <span>{opt}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="ai-callout mt-3 text-sm">
                    <div className="overline text-[#002FA7]">{t("results.explanation")}</div>
                    {q.explanation}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
